// App.tsx — defines the route table for the entire knowledge base.
//
// Each guide is a top-level route. The Layout component wraps them all to
// provide the top-bar, navbar, footer, and search modal.
//
// Adding a new guide is a 3-step process:
//   1. Create src/pages/<Role>Guide.tsx
//   2. Import it below
//   3. Add a <Route> entry to the ROUTES array

import { useEffect, type ReactElement } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

import Layout from './components/Layout';

// ----- Page components -----
// One per route. Kept as separate files because each guide is long enough
// to be unwieldy if inlined here.
import IndexPage from './pages/IndexPage';
import TeacherGuide from './pages/TeacherGuide';
import ParentGuide from './pages/ParentGuide';
import PrincipalGuide from './pages/PrincipalGuide';
import AccountantGuide from './pages/AccountantGuide';
import HrGuide from './pages/HrGuide';
import LibrarianGuide from './pages/LibrarianGuide';
import TransportGuide from './pages/TransportGuide';
import AdmissionsGuide from './pages/AdmissionsGuide';
import ReceptionGuide from './pages/ReceptionGuide';
import CounsellorGuide from './pages/CounsellorGuide';
import AdminGuide from './pages/AdminGuide';

// Shape of a route table entry. `as const` on the array (below) plus this
// interface gives us the route paths as a typed union when needed.
interface RouteDef {
  path: string;
  element: ReactElement;
  title: string;
}

// Route table — single source of truth for both the router and any
// downstream code that wants to enumerate pages (e.g. site map generator).
const ROUTES: RouteDef[] = [
  { path: '/',           element: <IndexPage />,       title: 'Help Center' },
  { path: '/teacher',    element: <TeacherGuide />,    title: "Teachers' Guide" },
  { path: '/parent',     element: <ParentGuide />,     title: "Parents' Guide" },
  { path: '/principal',  element: <PrincipalGuide />,  title: 'Principal & Coordinator Guide' },
  { path: '/accountant', element: <AccountantGuide />, title: 'Accountant & Cashier Guide' },
  { path: '/hr',         element: <HrGuide />,         title: 'HR & Payroll Manager Guide' },
  { path: '/librarian',  element: <LibrarianGuide />,  title: 'Librarian Guide' },
  { path: '/transport',  element: <TransportGuide />,  title: 'Transport In-charge Guide' },
  { path: '/admissions', element: <AdmissionsGuide />, title: 'Admissions & Enquiry Guide' },
  { path: '/reception',  element: <ReceptionGuide />,  title: 'Visitor Desk Guide' },
  { path: '/counsellor', element: <CounsellorGuide />, title: 'Counsellor Guide' },
  { path: '/admin',      element: <AdminGuide />,      title: 'System Administrator Guide' },
];

export default function App() {
  const location = useLocation();

  // Update document.title on route change. Single source of truth lives in
  // ROUTES above. The suffix is the site name shown in the browser tab.
  useEffect(() => {
    const match = ROUTES.find((r) => r.path === location.pathname);
    if (match) {
      // For the home page, "Help Center" alone is enough — no suffix needed.
      document.title = match.path === '/'
        ? match.title
        : `${match.title} | Help Center`;
    }

    // Restore scroll to top on route change. Without this, navigating
    // between long guides leaves you scrolled to the previous page's
    // position.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <Layout>
      <Routes>
        {ROUTES.map(({ path, element }) => (
          <Route key={path} path={path} element={element} />
        ))}
      </Routes>
    </Layout>
  );
}
